import { useEffect, useMemo, useState } from 'react';
import { layoutDag } from '../../utils/dagLayout';

/*
 * A specimen's lifecycle as a node graph, n8n-style: one card per stage,
 * arrows between them, left to right.
 *
 * Hand-drawn SVG, like the app's charts (MonthlyBarChart):
 * the graph is a few dozen nodes at most and its layout is the pure
 * utils/dagLayout.js. Colours are theme tokens set through `style`, because
 * var() is not valid in an SVG presentation attribute.
 *
 * What the user can do, when `canEdit`:
 *   - click a stage            select it (the page shows its editor)
 *   - its "+" handle           add a stage after it
 *   - click an arrow           then "+" to add a stage in between, or remove it
 *   - "connect" mode           click one stage, then another, to draw an arrow
 * Every change goes to the server through the page's callbacks; this
 * component holds nothing but what is selected.
 */

export const TYPE_COLOR = {
  field_collection: 'var(--c-success)',
  lab_subculture: 'var(--c-info)',
  storage: 'var(--c-purple)',
  dna_analysis: 'var(--c-warning)',
  custom: 'var(--c-text-muted)',
};
// Shared with the home hub's pipeline (components/hub/stages.js), so a stage
// looks the same on both canvases.
export const TYPE_ICON = { field_collection: '🌲', lab_subculture: '🧫', storage: '❄', dna_analysis: '🧬', custom: '◆' };
const STATUS_COLOR = {
  done: 'var(--c-success)',
  in_progress: 'var(--c-info)',
  planned: 'var(--c-border-strong)',
  skipped: 'var(--c-text-faint)',
};

/* Focus is shown on the arrow, the card or a ring round the button, not as
 * the browser's ring around an invisible hit area: an arrow's is 14 px wide
 * for a mouse and 44 px on a touch screen (index.css), and the round buttons
 * sit inside 44 px circles of their own.
 *
 * A round button's ring is a circle of its own, a gap away from the disc and
 * in the text colour. Restroking the disc in --c-info did nothing visible on
 * the insert button, which is filled with --c-info, nor on the "+" of a
 * lab_subculture card, which is outlined in it. */
const FOCUS_RING = 'var(--c-text-fg)';
const FOCUS_CSS = '.pg-hit,.pg-node,.pg-act{outline:none}'
  + '.pg-hit:focus-visible+.pg-line{stroke:var(--c-info)!important;stroke-width:3px!important}'
  + '.pg-node:focus-visible .pg-card{stroke:var(--c-info)!important;stroke-width:3px!important}'
  + `.pg-act:focus-visible .pg-focus{stroke:${FOCUS_RING};stroke-width:2px}`;
// 3 px of background between the 12 px disc and the ring; the stroke="none"
// attribute that hides it otherwise loses to that rule, as attributes do.
const FOCUS_R = 16;

/* A finger needs 44 x 44 px, and this SVG is drawn 1:1 in CSS pixels, so a
 * button's visible circle (22-24 px across) is not enough to aim at. Each
 * gets a transparent r=22 circle under it -- transparent, not `none`, so it
 * still takes the tap. Sized against utils/dagLayout.js: the "+" on a card's
 * edge reaches 22 px into the 92 px gap between columns, and an arrow's two
 * buttons, stacked above and below its middle, meet without overlapping. */
const TOUCH_R = 22;
const EDGE_BTN_Y = 22;

const clip = (text, n) => (text.length > n ? `${text.slice(0, n - 1)}…` : text);

/** The records the bank holds that belong to a standard stage. */
export function evidenceFor(stage, linked) {
  if (!stage.is_canonical || !linked) return [];
  switch (stage.stage_type) {
    case 'field_collection': return linked.origin ? [linked.origin.code || '—'] : [];
    case 'lab_subculture': return (linked.plates || []).map((p) => p.code);
    case 'storage': return (linked.slants || []).map((s) => s.code);
    case 'dna_analysis': return [...(linked.dna || []), ...(linked.runs || [])].map((r) => r.code);
    default: return [];
  }
}

export default function ProvenanceGraph({
  stages, edges, linked, selectedId, onSelect, canEdit = false,
  onAppend, onInsert, onConnect, onRemoveEdge, stageTitle, t,
}) {
  const [connectFrom, setConnectFrom] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState(null);

  const layout = useMemo(() => layoutDag(
    stages.map((s) => ({ id: s.stage_id })),
    edges.map((e) => ({ id: e.edge_id, from: e.from, to: e.to })),
  ), [stages, edges]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { setConnecting(false); setConnectFrom(null); setSelectedEdge(null); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const { node } = layout;
  const pick = (stageId) => {
    setSelectedEdge(null);
    if (connecting) {
      if (!connectFrom) { setConnectFrom(stageId); return; }
      if (connectFrom !== stageId) onConnect?.(connectFrom, stageId);
      setConnectFrom(null);
      setConnecting(false);
      return;
    }
    onSelect?.(stageId);
  };
  const keyed = (fn) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); }
  };

  const edgeById = Object.fromEntries(layout.paths.map((p) => [p.id, p]));
  const chosen = selectedEdge && edgeById[selectedEdge];

  return (
    <div>
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <button type="button" data-testid="graph-connect" aria-pressed={connecting}
            title={t('graph.tip.connect')}
            onClick={() => { setConnecting((c) => !c); setConnectFrom(null); setSelectedEdge(null); }}
            style={{
              background: connecting ? 'var(--c-info)' : 'var(--c-surface)',
              color: connecting ? 'var(--c-on-accent)' : 'var(--c-text-fg)',
              border: '1px solid var(--c-border-strong)', borderRadius: 6, padding: '6px 12px',
              cursor: 'pointer', fontSize: 13,
            }}>
            {connecting ? t('graph.cancelConnect') : t('graph.connect')}
          </button>
          {connecting && (
            <span style={{ fontSize: 13, color: 'var(--c-info-fg)' }} role="status">
              {connectFrom ? t('graph.connectTo') : t('graph.connectFrom')}
            </span>
          )}
        </div>
      )}
      <div dir="ltr" style={{ overflowX: 'auto', border: '1px solid var(--c-border)', borderRadius: 10,
        background: 'var(--c-bg-subtle)' }}>
        <svg width={layout.width} height={layout.height} role="group" aria-label={t('graph.label')}
          style={{ display: 'block', minWidth: '100%' }} data-testid="provenance-graph">
          <style>{FOCUS_CSS}</style>
          <defs>
            <marker id="pg-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7"
              orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" style={{ fill: 'var(--c-text-muted)' }} />
            </marker>
          </defs>

          {layout.paths.map((p) => (
            <g key={p.id}>
              {canEdit && (
                // A wide invisible stroke, so an arrow is easy to hit. It comes
                // first so that its focus can restyle the visible line after it.
                // The width is an attribute, not style, so that index.css can
                // widen it for a finger.
                <path d={p.d} data-testid={`edge-${p.id}`} role="button" tabIndex={0} className="pg-hit"
                  aria-label={t('graph.edgeLabel')} aria-pressed={selectedEdge === p.id}
                  onClick={() => setSelectedEdge(selectedEdge === p.id ? null : p.id)}
                  onKeyDown={keyed(() => setSelectedEdge(selectedEdge === p.id ? null : p.id))}
                  strokeWidth={14}
                  style={{ fill: 'none', stroke: 'transparent', cursor: 'pointer' }} />
              )}
              <path d={p.d} markerEnd="url(#pg-arrow)" className="pg-line"
                style={{ fill: 'none', stroke: selectedEdge === p.id ? 'var(--c-info)' : 'var(--c-text-muted)',
                  strokeWidth: selectedEdge === p.id ? 2.5 : 1.6, pointerEvents: 'none' }} />
            </g>
          ))}

          {stages.map((stage) => {
            const pos = layout.positions[stage.stage_id];
            if (!pos) return null;
            const color = TYPE_COLOR[stage.stage_type] || TYPE_COLOR.custom;
            const selected = selectedId === stage.stage_id;
            const source = connectFrom === stage.stage_id;
            const evidence = evidenceFor(stage, linked);
            const title = clip(stageTitle(stage), 22);
            const when = stage.occurred_at ? String(stage.occurred_at).slice(0, 10) : '';
            return (
              <g key={stage.stage_id} transform={`translate(${pos.x}, ${pos.y})`}>
                <g role="button" tabIndex={0} data-testid={`stage-${stage.stage_id}`} className="pg-node"
                  aria-label={`${stageTitle(stage)} — ${t(`stage.status.${stage.status}`)}`}
                  aria-pressed={selected}
                  onClick={() => pick(stage.stage_id)} onKeyDown={keyed(() => pick(stage.stage_id))}
                  style={{ cursor: 'pointer', opacity: stage.status === 'skipped' ? 0.55 : 1 }}>
                  <rect width={node.w} height={node.h} rx={10} className="pg-card"
                    style={{ fill: 'var(--c-surface)', stroke: selected || source ? 'var(--c-info)' : 'var(--c-border-strong)',
                      strokeWidth: selected || source ? 2.5 : 1,
                      strokeDasharray: stage.status === 'planned' ? '5 4' : 'none' }} />
                  <rect width={6} height={node.h} rx={3} style={{ fill: color }} />
                  <text x={16} y={24} style={{ fontSize: 15, fill: 'var(--c-text-fg)' }}>{TYPE_ICON[stage.stage_type]}</text>
                  <text x={38} y={24} style={{ fontSize: 13, fontWeight: 700, fill: 'var(--c-text-fg)' }}>{title}</text>
                  <circle cx={20} cy={46} r={5} style={{ fill: STATUS_COLOR[stage.status] }} />
                  <text x={32} y={50} style={{ fontSize: 12, fill: 'var(--c-text-muted-fg)' }}>
                    {t(`stage.status.${stage.status}`)}{when ? ` · ${when}` : ''}
                  </text>
                  {(stage.record || evidence.length > 0) && (
                    <text x={16} y={74} style={{ fontSize: 11, fill: 'var(--c-text-muted-fg)', fontFamily: 'monospace' }}>
                      {stage.record
                        ? clip(`↳ ${stage.record.code}${stage.record.deleted ? ` (${t('graph.deleted')})` : ''}`, 30)
                        : clip(evidence.length > 1 ? t('graph.records', { count: evidence.length, first: evidence[0] })
                          : evidence[0], 30)}
                    </text>
                  )}
                </g>
                {canEdit && (
                  <g role="button" tabIndex={0} data-testid={`append-${stage.stage_id}`} className="pg-act"
                    aria-label={t('graph.append')} onClick={() => onAppend?.(stage.stage_id)}
                    onKeyDown={keyed(() => onAppend?.(stage.stage_id))} style={{ cursor: 'pointer' }}>
                    <circle className="pg-touch" cx={node.w} cy={node.h / 2} r={TOUCH_R} fill="transparent" />
                    <circle className="pg-focus" cx={node.w} cy={node.h / 2} r={FOCUS_R} fill="none" stroke="none" />
                    <title>{t('graph.append')}</title>
                    <circle cx={node.w} cy={node.h / 2} r={11}
                      style={{ fill: 'var(--c-surface)', stroke: color, strokeWidth: 2 }} />
                    <text x={node.w} y={node.h / 2 + 5} textAnchor="middle"
                      style={{ fontSize: 16, fontWeight: 700, fill: 'var(--c-text-fg)' }}>+</text>
                  </g>
                )}
              </g>
            );
          })}

          {canEdit && chosen && (
            <g transform={`translate(${chosen.mid.x}, ${chosen.mid.y})`}>
              {/* One above the arrow, one below: side by side they were 30 px
                  apart, close enough for a thumb to remove an arrow it meant
                  to add a stage to. */}
              <g role="button" tabIndex={0} data-testid="edge-insert" aria-label={t('graph.insert')} className="pg-act"
                onClick={() => { setSelectedEdge(null); onInsert?.(chosen); }}
                onKeyDown={keyed(() => { setSelectedEdge(null); onInsert?.(chosen); })} style={{ cursor: 'pointer' }}>
                <circle className="pg-touch" cx={0} cy={-EDGE_BTN_Y} r={TOUCH_R} fill="transparent" />
                <circle className="pg-focus" cx={0} cy={-EDGE_BTN_Y} r={FOCUS_R} fill="none" stroke="none" />
                <title>{t('graph.insert')}</title>
                <circle cx={0} cy={-EDGE_BTN_Y} r={12} style={{ fill: 'var(--c-info)' }} />
                <text x={0} y={-EDGE_BTN_Y + 5} textAnchor="middle"
                  style={{ fontSize: 16, fontWeight: 700, fill: 'var(--c-on-accent)' }}>+</text>
              </g>
              <g role="button" tabIndex={0} data-testid="edge-remove" aria-label={t('graph.removeEdge')} className="pg-act"
                onClick={() => { setSelectedEdge(null); onRemoveEdge?.(chosen); }}
                onKeyDown={keyed(() => { setSelectedEdge(null); onRemoveEdge?.(chosen); })} style={{ cursor: 'pointer' }}>
                <circle className="pg-touch" cx={0} cy={EDGE_BTN_Y} r={TOUCH_R} fill="transparent" />
                <circle className="pg-focus" cx={0} cy={EDGE_BTN_Y} r={FOCUS_R} fill="none" stroke="none" />
                <title>{t('graph.removeEdge')}</title>
                <circle cx={0} cy={EDGE_BTN_Y} r={12} style={{ fill: 'var(--c-danger)' }} />
                <text x={0} y={EDGE_BTN_Y + 5} textAnchor="middle"
                  style={{ fontSize: 14, fontWeight: 700, fill: 'var(--c-on-accent)' }}>✕</text>
              </g>
            </g>
          )}
        </svg>
      </div>
      <ul style={{ display: 'flex', gap: 14, flexWrap: 'wrap', listStyle: 'none', padding: 0, margin: '8px 0 0',
        fontSize: 12, color: 'var(--c-text-muted-fg)' }} aria-label={t('graph.legend')}>
        {Object.keys(TYPE_COLOR).map((type) => (
          <li key={type} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 2, background: TYPE_COLOR[type] }} />
            {t(`stage.types.${type}`)}
          </li>
        ))}
        <li>{t('graph.dashedPlanned')}</li>
      </ul>
    </div>
  );
}
